import { TFunction } from 'i18next';
import {
  getCodeValidation,
  getEmailValidation,
  getFeatureCollectionValidation,
  getFieldValidation,
  getOptionsArrayValidation,
  getOptionValidation,
  getPhoneValidation,
} from '@lib/utils/validationUtils';
import _get from 'lodash/get';
import Input from '@lib/components/Input/Input';
import { InputTypes } from '@lib/components/Input/enums';
import { FieldItem, Values } from '@lib/interfaces/form';
import { MAX_TEXT_EDITOR_FIELD_LENGTH, Steps } from '@lib/enums/form';
import Typography from '@lib/components/Typography/Typography';
import Select, { SelectComponents } from '@lib/components/Select/Select';
import ImageUploader from '@lib/components/ImageUploader/ImageUploader';
import UserOption from '@lib/components/Select/components/UserOption';
import { GetLoadOptions, LoadOptions } from '@lib/components/Select/types';
import UserSingleValue from '@lib/components/Select/components/UserSingleValue';
import { SitePointerShape } from '@lib/components/Map/enums';
import { SiteFieldNames } from '@lib/enums/fieldNames/siteFieldNames';
import CheckBoxSingleBool from '@lib/components/CheckBoxSingleBool/CheckBoxSingleBool';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import TextEditor from '@lib/components/TextEditor/TextEditor';
import SectorOption from '@lib/components/Select/components/SectorOption';
import SectorSingleValue from '@lib/components/Select/components/SectorSingleValue';
import UserChipListField from '@lib/components/UserChipListField/UserChipListField';
import MultiSelectValueContainer from '@lib/components/Select/components/MultiSelectValueContainer';
import SupportiveMessage, {
  SupportiveMessageColor,
} from '@lib/components/SupportiveMessage/SupportiveMessage';
import { Site } from 'graphql-common';
import Map from '@lib/components/Map/Map';
import RadioButtonGroup, {
  RadioButtonWrapViews,
} from '@lib/components/RadioButtonGroup/RadioButtonGroup';
import RadioButtonAsButton from '@lib/components/RadioButtonAsButton/RadioButtonAsButton';
import IconUploader from '@lib/components/IconUploader/IconUploader';
import getColorOptions from '@lib/components/Chip/utils/colorEnum';
import RadioButtonAsChipColor from '@lib/components/RadioButtonAsChipColor/RadioButtonAsChipColor';
import { addWhiteToIconUrl } from 'routes/Sites/utils/addWhiteToIconUrl';
import formStyles from '@lib/assets/styles/Form.module.scss';

const areaMultiSelectComponents = {
  ValueContainer: MultiSelectValueContainer,
};

type AreaFormFieldsArgs = {
  getDocumentationsLoadOptions?: GetLoadOptions;
  isEditForm?: boolean;
  openDocumentsModal?: (name?: string) => void;
  t: TFunction<'translation', undefined>;
};

// The documents sub-field renders only when both `getDocumentationsLoadOptions`
// and `openDocumentsModal` are supplied. Omitted in flows that shouldn't expose
// the field (e.g. SiteEdit, where per-row unlink diffs can't be computed).
export function getAreaFields({
  getDocumentationsLoadOptions,
  isEditForm,
  openDocumentsModal,
  t,
}: AreaFormFieldsArgs): FieldItem[] {
  return [
    {
      name: SiteFieldNames.Name,
      label: t('area-name'),
      element: Input,
      isUnique: true,
      validation: getFieldValidation({ required: true, t }),
    },
    {
      name: SiteFieldNames.Code,
      label: t('area-code'),
      element: Input,
      isUnique: true,
      validation: getCodeValidation({ t, max: 10, required: true }),
      componentProps: {
        disabled: isEditForm,
      },
    },
    {
      name: SiteFieldNames.Address,
      label: t('area-address'),
      element: Input,
      validation: getFieldValidation({ t }),
    },
    {
      name: SiteFieldNames.Description,
      label: t('area-description'),
      element: TextEditor,
      validation: getFieldValidation({
        maxLength: MAX_TEXT_EDITOR_FIELD_LENGTH,
        t,
      }),
    },
    ...(getDocumentationsLoadOptions && openDocumentsModal
      ? [
          {
            name: SiteFieldNames.DocumentationLinks,
            label: t('documents'),
            element: Select,
            isOptionalFn: () => true,
            componentProps: {
              isAsync: true,
              isMulti: true,
              getLoadOptions: getDocumentationsLoadOptions,
              defaultOptions: false,
              isClearable: false,
              isSearchable: false,
              openMenuOnClick: false,
              openMenuOnFocus: false,
              menuIsOpen: false,
              onSelectWrapperMouseDown: openDocumentsModal,
              components: areaMultiSelectComponents,
            },
          } as FieldItem,
        ]
      : []),
    {
      name: SiteFieldNames.PoorWifi,
      label: t('area-poor-wifi-label'),
      element: CheckBoxSingleBool,
      componentProps: {
        options: [{ label: 'checked', value: 'checked' }],
        rightLabelIcon: <MaterialIcon icon="wifi_off" size="md-16" />,
      },
    },
  ];
}

type FormFieldsArgs = {
  site?: Site;
  countriesCodes: string[];
  getDocumentationsLoadOptions?: GetLoadOptions;
  openDocumentsModal?: (name?: string) => void;
  sectorsLoadOptions: LoadOptions;
  usersLoadOptions: LoadOptions;
  isEditForm?: boolean;
  isSectorRequired?: boolean;
  openUserFormModal: () => void;
  t: TFunction<'translation', undefined>;
  responsiblePersonLoadOptions: LoadOptions;
  assigneeIds: { id: string }[];
};

export function getSiteFields({
  assigneeIds,
  countriesCodes,
  isEditForm,
  isSectorRequired,
  openUserFormModal,
  responsiblePersonLoadOptions,
  sectorsLoadOptions,
  site,
  t,
  usersLoadOptions,
}: FormFieldsArgs): FieldItem[] {
  return [
    {
      name: SiteFieldNames.SiteDetails,
      customRender: () => (
        <Typography variant="h3" key="site-details">
          {t('site-details')}
        </Typography>
      ),
      step: Steps.step1,
    },
    {
      name: SiteFieldNames.Name,
      label: t('site-name'),
      element: Input,
      validation: getFieldValidation({ required: true, t }),
      step: Steps.step1,
    },
    {
      name: SiteFieldNames.Code,
      label: t('site-code'),
      element: Input,
      validation: getCodeValidation({ t, max: 10, required: true }),
      step: Steps.step1,
      componentProps: {
        disabled: isEditForm,
      },
    },
    {
      name: SiteFieldNames.Description,
      label: t('site-description'),
      element: TextEditor,
      validation: getFieldValidation({
        maxLength: MAX_TEXT_EDITOR_FIELD_LENGTH,
        t,
      }),
      step: Steps.step1,
    },
    {
      name: SiteFieldNames.Sector,
      label: t('sector'),
      element: Select,
      validation: getOptionValidation({ required: isSectorRequired, t }),
      componentProps: {
        isAsync: true,
        loadOptions: sectorsLoadOptions,
        defaultOptions: false,
        components: {
          Option: SectorOption,
          SingleValue: SectorSingleValue,
        },
      },
      // Sector-less sites land in 'Other', so the hint is wrong for users
      // who must pick one of their own sectors.
      description: isSectorRequired
        ? undefined
        : t('site-sector-field-description'),
      step: Steps.step1,
    },
    {
      name: SiteFieldNames.SectorWarning,
      customRender: () => (
        <SupportiveMessage
          text={t('site-sector-field-warning')}
          color={SupportiveMessageColor.Yellow}
        />
      ),
      step: Steps.step1,
      hideErrorsSpace: true,
      isVisibleFn: (values: Values, name?: string) => {
        const prevSectorId = site?.sector?.id;
        if (name && prevSectorId) {
          return (
            _get(values, [SiteFieldNames.Sector, 'value']) !== prevSectorId
          );
        }
        return false;
      },
    },
    {
      name: SiteFieldNames.UserMembershipsTitle,
      customRender: () => (
        <Typography
          variant="h3"
          key="contact-info"
          className={formStyles.blockStart}
        >
          {t('assign-users-to-site')}
        </Typography>
      ),
      step: Steps.step1,
    },
    {
      name: SiteFieldNames.UserMemberships,
      label: t('find-users'),
      element: Select,
      validation: getOptionsArrayValidation({ t }),
      componentProps: {
        isAsync: true,
        isMulti: true,
        loadOptions: usersLoadOptions,
        defaultOptions: false,
        components: {
          Option: UserOption,
          ValueContainer: MultiSelectValueContainer,
        } as unknown as SelectComponents,
      },
      step: Steps.step1,
      description: t('full-admins-not-listed'),
      hideErrorsSpace: true,
    },
    {
      name: SiteFieldNames.UserMembershipsList,
      element: UserChipListField,
      componentProps: {
        fieldName: SiteFieldNames.UserMemberships,
        siteId: site?.id,
        ids: assigneeIds,
      },
      isUseWatch: true,
      step: Steps.step1,
    },
    {
      name: SiteFieldNames.ContactInfo,
      customRender: () => (
        <Typography
          variant="h3"
          key="contact-info"
          className={formStyles.blockStart}
        >
          {t('contact-info')}
        </Typography>
      ),
      step: Steps.step1,
    },
    {
      name: SiteFieldNames.PhoneNumber,
      label: t('site-phone-number'),
      element: Input,
      componentProps: {
        type: InputTypes.tel,
        onlyCountries: countriesCodes,
      },
      validation: getPhoneValidation({ required: false, t }),
      formItemClassName: formStyles.formColumn,
      step: Steps.step1,
    },
    {
      name: SiteFieldNames.Email,
      label: t('site-email'),
      element: Input,
      validation: getEmailValidation({ required: true, t }),
      formItemClassName: formStyles.formColumn,
      step: Steps.step1,
    },
    {
      name: SiteFieldNames.UserId,
      label: t('responsible-person'),
      description: t('responsible-person-description'),
      element: Select,
      validation: getOptionValidation({ t, required: true }),
      componentProps: {
        isAsync: true,
        loadOptions: responsiblePersonLoadOptions,
        defaultOptions: false,
        components: {
          Option: UserOption,
          SingleValue: UserSingleValue,
        } as unknown as SelectComponents,
        createNewOptionProps: {
          title: t('create-new'),
          onCLick: openUserFormModal,
        },
      },
      step: Steps.step1,
    },
    {
      name: SiteFieldNames.LocationInfo,
      customRender: () => (
        <Typography
          variant="h3"
          key="location-info"
          className={formStyles.blockStart}
        >
          {t('site-location-and-pointer')}
        </Typography>
      ),
      step: Steps.step1,
    },
    {
      name: SiteFieldNames.Address,
      label: t('site-address-details'),
      element: Input,
      validation: getFieldValidation({ t }),
      step: Steps.step1,
    },
    {
      name: SiteFieldNames.LocationPoint,
      element: Map,
      isUseWatch: true,
      validation: getFeatureCollectionValidation({ required: true, t }),
      getComponentProps: (values: Values) => {
        const shape = _get(values, [SiteFieldNames.PointerShape, 'value']);
        const color = _get(values, [SiteFieldNames.PointerColor, 'value']);
        const icon = _get(values, [
          SiteFieldNames.PointerIconAttached,
          'fileTag',
        ]);
        return {
          maxMarkers: 1,
          pointerProps: {
            shape,
            // `color` is the `ColorEnum` form value; the marker resolves it to a
            // `chip-*` class via `colorToChipType`.
            color,
            icon: icon ? addWhiteToIconUrl(icon) : undefined,
          },
        };
      },
      step: Steps.step1,
    },
    {
      name: SiteFieldNames.SitePointerDesign,
      customRender: () => (
        <Typography
          variant="body"
          strong
          key="location-info"
          className={formStyles.blockStart}
        >
          {t('site-pointer-design')}
        </Typography>
      ),
      step: Steps.step1,
    },
    {
      name: SiteFieldNames.PointerShape,
      label: t('shape'),
      element: RadioButtonGroup,
      validation: getOptionValidation({ required: true, t }),
      componentProps: {
        options: [
          {
            label: t('circle'),
            value: SitePointerShape.Circle,
            leftIcon: 'radio_button_unchecked',
          },
          {
            label: t('square'),
            value: SitePointerShape.Square,
            leftIcon: 'check_box_outline_blank',
          },
          {
            label: t('star'),
            value: SitePointerShape.Star,
            leftIcon: 'brightness_empty',
          },
          {
            label: t('pentagon'),
            value: SitePointerShape.Pentagon,
            leftIcon: 'pentagon',
          },
        ],
        RadioButtonComponent: RadioButtonAsButton,
        wrapView: RadioButtonWrapViews.row,
      },
    },
    {
      name: SiteFieldNames.PointerColor,
      label: t('color'),
      element: RadioButtonGroup,
      validation: getOptionValidation({ required: true, t }),
      componentProps: {
        options: getColorOptions({ t }),
        RadioButtonComponent: RadioButtonAsChipColor,
        wrapView: RadioButtonWrapViews.colors,
      },
    },
    {
      name: SiteFieldNames.PointerIconAttached,
      label: '',
      element: IconUploader,
    },
    {
      name: SiteFieldNames.LocationPhoto,
      customRender: () => (
        <Typography
          variant="h3"
          key="location-photo"
          className={formStyles.blockStart}
        >
          {t('upload-location-photo')}
        </Typography>
      ),
      step: Steps.step1,
    },
    {
      name: SiteFieldNames.LocationPhotoAttached,
      element: ImageUploader,
      step: Steps.step1,
    },
  ];
}

type CodeConductFormFieldsArgs = {
  step?: Steps;
  subFieldEmptyStateText?: string;
  subFieldEmptyStateTitle?: string;
  t: TFunction<'translation', undefined>;
};

export function getCodeConductFields({
  step,
  subFieldEmptyStateTitle,
  subFieldEmptyStateText,
  t,
}: CodeConductFormFieldsArgs): FieldItem[] {
  return [
    {
      name: SiteFieldNames.ConductCodeSections,
      subFieldEmptyStateTitle,
      subFieldEmptyStateText,
      subFieldTitle: t('section'),
      subFieldsEntity: 'section',
      subFields: [
        {
          name: SiteFieldNames.Title,
          label: t('section-title'),
          element: Input,
          isUnique: true,
          validation: getFieldValidation({
            t,
            required: true,
          }),
        },
        {
          name: SiteFieldNames.Body,
          label: t('section-text'),
          element: Input,
          componentProps: {
            type: InputTypes.textarea,
          },
          isUnique: true,
          validation: getFieldValidation({
            t,
            required: true,
          }),
        },
      ],
      step,
      isDraggable: true,
    },
  ];
}

export default function getSiteFormFields({
  assigneeIds,
  countriesCodes,
  getDocumentationsLoadOptions,
  isEditForm,
  isSectorRequired,
  openDocumentsModal,
  openUserFormModal,
  responsiblePersonLoadOptions,
  sectorsLoadOptions,
  site,
  t,
  usersLoadOptions,
}: FormFieldsArgs): FieldItem[] {
  return [
    ...getSiteFields({
      assigneeIds,
      countriesCodes,
      isEditForm,
      isSectorRequired,
      openUserFormModal,
      responsiblePersonLoadOptions,
      sectorsLoadOptions,
      site,
      t,
      usersLoadOptions,
    }),
    {
      name: SiteFieldNames.Areas,
      subFieldEmptyStateTitle: t('create-areas-empty-state-title'),
      subFieldEmptyStateText: t('create-areas-empty-state-text'),
      subFieldTitle: t('area'),
      subFieldsEntity: 'area',
      subFields: getAreaFields({
        getDocumentationsLoadOptions,
        openDocumentsModal,
        t,
      }),
      step: Steps.step2,
    },
    ...getCodeConductFields({
      t,
      step: Steps.step3,
      subFieldEmptyStateTitle: t('create-code-of-conduct-empty-state-title'),
      subFieldEmptyStateText: t('create-code-of-conduct-empty-state-text'),
    }),
  ];
}
